import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  isNativePlatform,
  parseDeepLinkAuthTarget,
  closeExternalAuthBrowser,
} from "../../lib/nativeAuth";
import { appendAuthStep } from "../../lib/authTrace";
import { parseAppLinkTarget } from "../../lib/appLinks";

/**
 * Native-only bridge that catches the OAuth / password-reset deep link and
 * hands it to the existing in-app callback route. See #83.
 *
 * Flow on Android:
 *   1. User taps "Continue with Google"; the provider URL opens in a Custom Tab.
 *   2. Supabase finishes and redirects to com.cardonomer.app://auth/callback?code=...
 *   3. The manifest's VIEW/BROWSABLE intent filter delivers that URL to the
 *      already-running app, which fires Capacitor's `appUrlOpen`.
 *   4. We close the tab and navigate to /auth/callback with the query/hash
 *      intact, so AuthCallbackPage completes the PKCE exchange exactly as on web.
 *
 * B-43: the same listener also takes the website links in trade emails
 * (https://<site>/trades/<id>, /chat?threadId=<id>), which the manifest claims
 * as verified App Links, and opens that screen in the app. Those are pushed,
 * not replaced, so Back returns to wherever the user was. If the app was not
 * running, Capacitor holds the link until this listener attaches.
 *
 * A no-op on web: the listener is never registered, so nothing changes for the
 * browser build, and @capacitor/app is imported lazily so the web bundle and
 * the Jest environment never load it.
 */
const useAuthDeepLink = () => {
  const navigate = useNavigate();

  useEffect(() => {
    if (!isNativePlatform()) return undefined;

    let removeListener = () => {};
    let cancelled = false;

    (async () => {
      const { App } = await import("@capacitor/app");
      if (cancelled) return;

      const handle = await App.addListener("appUrlOpen", ({ url }) => {
        const target = parseDeepLinkAuthTarget(url);
        if (!target) {
          // Not an auth callback: a trade or chat link from an email (B-43),
          // or nothing we accept, which is ignored.
          const appTarget = parseAppLinkTarget(url);
          if (appTarget) navigate(appTarget);
          return;
        }
        appendAuthStep("nativeAuthDeepLinkReceived");
        closeExternalAuthBrowser();
        // React Router owns navigation; the query/hash ride along so
        // AuthCallbackPage reads `code` off window.location as it always has.
        navigate(target, { replace: true });
      });

      // addListener resolves to a handle with its own remove(); keep it so the
      // effect cleanup detaches exactly one listener.
      removeListener = () => handle.remove();
      if (cancelled) removeListener();
    })();

    return () => {
      cancelled = true;
      removeListener();
    };
  }, [navigate]);
};

export default useAuthDeepLink;
